import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL, UPLOADS_BASE_URL } from "../../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./employeePrint.css";

const EmployeeOverallReport = () => {
  const { requestId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [emp, setEmp] = useState(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [comments, setComments] = useState({});
  const [hrUse, setHrUse] = useState(null);

  /* The score sheet is letterheaded with the school logo - never the employee photo */
  const LOGO_URL = `${UPLOADS_BASE_URL}/uploads/logo/favicon/aaps.jpg`;

  /* Blank rather than 0 when there is no salary row, as the printed sheet does */
  const money = (v) =>
    v === null || v === undefined
      ? ""
      : Number(v).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });


  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/employees-appraisal/overall-report/${requestId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((res) => {
        setEmp(res.data.employee);
        setRows(res.data.questions || []);
        setTotals(res.data.totals || {});
        setComments(res.data.comments || {});
        setHrUse(res.data.hr_use || null);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load report");
        history.push("/EmployeesAppraisalList");
      });
  }, [requestId, history]);

  if (loading || !emp) return null;

  const calculateTotals = () => {
    let grandTotal = 0;
    let grandAvg = 0;
    rows.forEach(r => {
      const scores = [
        r.self, r.incharge, r.hod,
        r.hr, r.management, r.others,
      ].filter(v => v !== null);

      const total = scores.reduce((a, b) => a + b, 0);
      const avg = scores.length ? Math.round(total / scores.length) : 0;

      grandTotal += total;
      grandAvg += avg;
    });
    return { grandTotal, grandAvg };
  };

  const { grandTotal, grandAvg } = calculateTotals();

  return (
    <div className="container-fluid">
      <ToastContainer />

      {/* ===== ACTION BAR ===== */}
      <div className="card mb-3 no-print">
        <div className="card-body d-flex justify-content-between">
          <h5>Employee Appraisal – Overview Report</h5>
          <div>
            <button
              className="btn btn-secondary mr-2"
              onClick={() => history.push("/EmployeesAppraisalList")}
            >
              Back
            </button>

            <button
              className="btn btn-primary"
              onClick={() =>
                window.open(
                  `${API_BASE_URL}/employees-appraisal/overall-report-pdf/${requestId}`,
                  "_blank"
                )
              }
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>

      {/* ===== PRINT AREA ===== */}
      <div className="pdf-wrapper">

        {/* ===== HEADER ===== */}
        <div className="print-header">
          <div className="header-row">
            <div className="logo-box">
              <img src={LOGO_URL} alt="Annai Arul Public School" className="hospital-logo" />
            </div>

            <div className="title-box">
              <h2>Annai Arul Public School</h2>
              <h4>Performance Appraisal Score Sheet</h4>
            </div>
          </div>
        </div>

        {/* ===== EMPLOYEE INFO ===== */}
        <table className="info-table">
          <tbody>
            <tr>
              <td><b>Employee Name :</b> {emp.emp_name}</td>
              <td><b>Employee ID :</b> {emp.employee_id}</td>
            </tr>
            <tr>
              <td><b>Designation :</b> {emp.designation_name}</td>
              <td><b>Date Of Joining :</b> {emp.date_of_joining}</td>
            </tr>
            <tr>
              <td><b>Department :</b> {emp.department_name}</td>
              <td><b>Month Of Appraisal :</b> {emp.appraisal_month}</td>
            </tr>
          </tbody>
        </table>

        {/* ===== SCORE TABLE ===== */}
        <div className="table-scroll">
        <table className="score-table">
          <thead>
            <tr>
              <th>S.N</th>
              <th>Questions</th>
              <th>Self</th>
              <th>Incharge</th>
              <th>HOD</th>
              <th>HR</th>
              <th>Management</th>
              <th>Others</th>
              <th>Total Score</th>
              <th>Average Score</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r, i) => {
              const scores = [
                r.self, r.incharge, r.hod,
                r.hr, r.management, r.others,
              ].filter(v => v !== null);

              const total = scores.reduce((a, b) => a + b, 0);
              const avg = scores.length ? Math.round(total / scores.length) : "--";

              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td className="text-left">{r.question_title}</td>
                  <td>{r.self ?? "--"}</td>
                  <td>{r.incharge ?? "--"}</td>
                  <td>{r.hod ?? "--"}</td>
                  <td>{r.hr ?? "--"}</td>
                  <td>{r.management ?? "--"}</td>
                  <td>{r.others ?? "--"}</td>
                  <td>{total || "--"}</td>
                  <td>{avg}</td>
                </tr>
              );
            })}
            {/* ===== EVALUATOR SUMMARY TOTALS ===== */}
            <tr>
              <td colSpan="2" style={{ textAlign: "right", fontWeight: "bold" }}>Total</td>
              <td style={{ fontWeight: "bold" }}>{totals.self ?? "--"}</td>
              <td style={{ fontWeight: "bold" }}>{totals.incharge ?? "--"}</td>
              <td style={{ fontWeight: "bold" }}>{totals.hod ?? "--"}</td>
              <td style={{ fontWeight: "bold" }}>{totals.hr ?? "--"}</td>
              <td style={{ fontWeight: "bold" }}>{totals.management ?? "--"}</td>
              <td colSpan="3"></td>
            </tr>

            {/* ===== EVALUATOR REMARKS ===== */}
            {["incharge", "hod", "hr", "management"].map((level) =>
              comments[level] ? (
                <tr key={level}>
                  <td colSpan="2" className="text-left" style={{ fontWeight: "bold" }}>
                    {comments[level].name}
                  </td>
                  <td colSpan="8" className="text-left">
                    {comments[level].comment}
                  </td>
                </tr>
              ) : null
            )}

            <tr>
              <td></td>
              <td className="text-left" style={{ fontWeight: "bold" }}>Total :</td>
              <td colSpan="8" style={{ textAlign: "right" }}>{grandTotal || "--"}</td>
            </tr>
            <tr>
              <td></td>
              <td className="text-left" style={{ fontWeight: "bold" }}>Average :</td>
              <td colSpan="8" style={{ textAlign: "right" }}>{grandAvg || "--"}</td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* ===== HR USE ===== */}
        {hrUse && (
          <div className="hr-use">
            <h5>HR USE</h5>
            <div className="table-scroll">
              <table className="score-table hr-use-table">
                <tbody>
                  <tr>
                    <td>No of days worked</td>
                    <td>Late coming days</td>
                    <td>No of feedback</td>
                    <td>Memo's/Warning letter</td>
                  </tr>
                  <tr>
                    <td>{hrUse.days_worked}</td>
                    <td>{hrUse.late_days}</td>
                    <td>{hrUse.feedback_count}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>Present salary(Gross)</td>
                    <td>Last increment</td>
                    <td>No of student reviews</td>
                    <td>HR Signature</td>
                  </tr>
                  <tr>
                    <td>{money(hrUse.salary?.gross)}</td>
                    <td>{money(hrUse.salary?.increment)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="sign-row">
              <div>
                <div>For use of Management</div>
                <div>Remarks:</div>
              </div>
              <div>Dean Signature</div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EmployeeOverallReport;
